import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Award,
  BriefcaseBusiness,
  Building2,
  Check,
  Clock3,
  Layers3,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { getAllCompetencies, getHrisDashboard } from '../hrisApi';
import CompetencyList from './CompetencyList';
import './HRISDashboard.css';

const cards = [
  { key: 'activeUsers', label: 'Active employees', icon: Users, color: '#16837d' },
  { key: 'activeDepartments', label: 'Active departments', icon: Building2, color: '#2d7dd2' },
  { key: 'activePositions', label: 'Active positions', icon: BriefcaseBusiness, color: '#8c5ad8' },
  { key: 'competencyProfiles', label: 'Competency profiles', icon: ShieldCheck, color: '#ca7b20' },
];

const demoCompetencies = [
  { id: 'demo-1', employeeName: 'Anwar Siregar', employeeId: '13', qualificationName: 'Auditor', standard: 'ISO/IEC 27001', sectors: 'Information Technology', status: 'approved' },
  { id: 'demo-2', employeeName: 'Anwar Siregar', employeeId: '13', qualificationName: 'Application Reviewer', standard: 'ISO/IEC 27001', sectors: 'Information Technology', status: 'approved' },
  { id: 'demo-3', employeeName: 'Anwar Siregar', employeeId: '13', qualificationName: 'Certification Authority', standard: 'ISO 37001', sectors: 'Governance', status: 'approved' },
  { id: 'demo-4', employeeName: 'Iwenda B. Subagio', employeeId: '86', qualificationName: 'Auditor', standard: 'ISO 9001', sectors: 'Manufacturing', status: 'approved' },
  { id: 'demo-5', employeeName: 'Iwenda B. Subagio', employeeId: '86', qualificationName: 'Auditor', standard: 'ISO 37001', sectors: 'Governance', status: 'approved' },
  { id: 'demo-6', employeeName: 'Iwenda B. Subagio', employeeId: '86', qualificationName: 'Certificate Reviewer', standard: 'ISO 37001', sectors: 'Governance', status: 'approved' },
  { id: 'demo-7', employeeName: 'Satrio Sandyakala Tutuko', employeeId: '97', qualificationName: 'Scheduler / Operation', standard: 'ISO/IEC 27001', sectors: 'Information Technology', status: 'approved' },
  { id: 'demo-8', employeeName: 'Satrio Sandyakala Tutuko', employeeId: '97', qualificationName: 'Scheduler / Operation', standard: 'ISO 9001', sectors: 'Manufacturing', status: 'approved' },
  { id: 'demo-9', employeeName: 'Dina Maharani', employeeId: '104', qualificationName: 'Lead Auditor', standard: 'ISO 14001', sectors: 'Environment', status: 'pending' },
  { id: 'demo-10', employeeName: 'Raka Pradipta', employeeId: '112', qualificationName: 'Technical Expert', standard: 'ISO 45001', sectors: 'Health & Safety', status: 'revision' },
];

const normalizeText = (value, fallback = 'Unspecified') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const countBy = (items, pickValue) => {
  const map = new Map();
  items.forEach((item) => {
    const key = normalizeText(pickValue(item));
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
};

const uniqueCount = (items, pickValue) => new Set(
  items.map((item) => normalizeText(pickValue(item), '')).filter(Boolean)
).size;

const buildChartOptions = ({ labels, colors, series = [], horizontal = false }) => ({
  chart: { toolbar: { show: false }, fontFamily: 'inherit' },
  colors,
  labels,
  // Built-in legend off for both chart types: the bar's single-series legend is just
  // noise, and the donut's legend is rendered as plain React markup below instead
  // (see DonutLegend) — a custom `legend.formatter` was the earlier crash trigger,
  // and plain JSX also gets us per-item counts the built-in legend couldn't show safely.
  legend: { show: false },
  // Bar: plain integer count above each bar. Donut: round to a whole percentage —
  // this ApexCharts version's default pie formatter emits long raw decimals
  // (e.g. "14.285714285714292%") that overlap on adjacent slices.
  dataLabels: {
    enabled: true,
    // Bar labels sit in white space above the bar (dark text is fine there). Donut
    // labels sit ON the colored ring itself — black text read poorly against the
    // lighter slices (light blue, purple), so it's white + a soft dark drop-shadow
    // instead, which stays legible against every slice color rather than picking a
    // different text color per slice.
    style: horizontal
      ? { fontSize: '11px', fontWeight: 700 }
      : { fontSize: '12px', fontWeight: 750, colors: ['#fff'] },
    dropShadow: horizontal
      ? { enabled: false }
      : { enabled: true, top: 0, left: 0, blur: 2, color: '#1f2b3d', opacity: 0.55 },
    formatter: horizontal ? (value) => `${value}` : (value) => `${Math.round(value)}%`,
  },
  stroke: { width: horizontal ? 0 : 2, colors: ['#fff'] },
  grid: { borderColor: '#edf2f7', strokeDashArray: 4 },
  plotOptions: horizontal
    ? { bar: { horizontal: true, borderRadius: 5, barHeight: '54%', dataLabels: { position: 'top' } } }
    : {
        pie: {
          donut: {
            // Slightly larger hole so the hover state's category name (can be long,
            // e.g. "Application Reviewer") has room without touching the ring.
            size: '76%',
            labels: {
              show: true,
              // Idle state: small "Total Records" caption + big sum.
              // Hover state (ApexCharts default): swaps to this same name/value pair
              // for the hovered slice — kept visually consistent on purpose.
              name: { show: true, fontSize: '12.5px', fontWeight: 600, color: '#7c8ea2', offsetY: -6 },
              value: {
                show: true,
                fontSize: '26px',
                fontWeight: 750,
                color: '#1f2b3d',
                offsetY: 2,
                formatter: (val) => val,
              },
              // Idle-state caption/number — deliberately NOT given the same fontSize as
              // name/value above: ApexCharts' `total` block styles only the big formatted
              // number, not its caption text, so a small fontSize here would shrink the
              // number, not the label. Left at its (already-correct) default size.
              total: {
                show: true,
                label: 'Total Records',
                formatter: () => `${series.reduce((sum, v) => sum + v, 0)}`,
              },
            },
          },
        },
      },
  xaxis: horizontal ? { categories: labels, labels: { style: { colors: '#718098' } } } : undefined,
  yaxis: horizontal ? { labels: { style: { colors: '#718098' } } } : undefined,
  // Donut tooltip-on-hover throws inside this ApexCharts version (same internal
  // "reading 'tooltip'" crash class as the earlier dataLabels issue) once a custom
  // donut.labels.total.formatter is set. Every number it would show (label, %, count)
  // is already visible via the slice label, legend, and center total, so disabling
  // it here trades an inert hover state for a chart that doesn't crash the page.
  tooltip: { enabled: horizontal, theme: 'light' },
});

const DonutLegend = ({ items, colors }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return (
    <div className="hris-donut-legend">
      {items.map((item, index) => (
        <div className="hris-donut-legend__item" key={item.label}>
          <span className="hris-donut-legend__dot" style={{ background: colors[index % colors.length] }} />
          <span className="hris-donut-legend__label" title={item.label}>{item.label}</span>
          <span className="hris-donut-legend__value">
            {item.value} <em>({total > 0 ? Math.round((item.value / total) * 100) : 0}%)</em>
          </span>
        </div>
      ))}
    </div>
  );
};

const MiniMetric = ({ icon: Icon, label, value, tone = 'teal' }) => (
  <div className={`hris-competency-mini hris-competency-mini--${tone}`}>
    <span><Icon size={18} /></span>
    <div>
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  </div>
);

const HRISDashboard = () => {
  const [metrics, setMetrics] = useState({});
  const [competencies, setCompetencies] = useState([]);
  const [isCompetencyLoading, setIsCompetencyLoading] = useState(true);
  const [competencyError, setCompetencyError] = useState('');
  const [usingDemo, setUsingDemo] = useState(false);

  useEffect(() => {
    getHrisDashboard().then(setMetrics).catch(() => setMetrics({}));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsCompetencyLoading(true);
    getAllCompetencies()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setUsingDemo(list.length === 0);
        setCompetencies(list.length > 0 ? list : demoCompetencies);
        setCompetencyError('');
      })
      .catch(() => {
        if (cancelled) return;
        setUsingDemo(true);
        setCompetencies(demoCompetencies);
        setCompetencyError('Live competency data could not be reached. Showing demo dashboard data.');
      })
      .finally(() => {
        if (!cancelled) setIsCompetencyLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const analytics = useMemo(() => {
    const approvedCompetencies = competencies.filter((item) => String(item.status || '').toLowerCase() === 'approved');
    const byProfile = countBy(competencies, (item) => item.qualificationName);
    // Standard Coverage is meant to answer "who's actually qualified per standard" —
    // approved-only, not diluted by pending/rejected/revision noise.
    const byStandardApproved = countBy(approvedCompetencies, (item) => item.standard);
    const approved = approvedCompetencies.length;
    const approvedRate = competencies.length > 0 ? Math.round((approved / competencies.length) * 100) : 0;
    const attention = competencies.filter((item) => ['pending', 'revision', 'need_revision', 'revision_requested'].includes(String(item.status || '').toLowerCase())).length;
    const people = uniqueCount(competencies, (item) => item.employeeName || item.employeeId);
    const profiles = uniqueCount(competencies, (item) => item.qualificationName);
    const standards = uniqueCount(competencies, (item) => item.standard);
    const topProfiles = byProfile.slice(0, 8);
    const topStandards = byStandardApproved.slice(0, 8);
    const peopleMap = new Map();
    competencies.forEach((item) => {
      const person = normalizeText(item.employeeName || item.employeeId);
      if (!peopleMap.has(person)) peopleMap.set(person, []);
      peopleMap.get(person).push(item);
    });
    const peopleRows = Array.from(peopleMap.entries())
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .slice(0, 8);
    const standardColumns = topStandards.slice(0, 6).map((item) => item.label);
    const matrix = peopleRows.map(([person, rows]) => ({
      person,
      total: rows.length,
      primaryRole: countBy(rows, (item) => item.qualificationName)[0]?.label || '-',
      standards: standardColumns.map((standard) => competencies.some((item) => (
        normalizeText(item.employeeName || item.employeeId) === person &&
        normalizeText(item.standard) === standard
      ))),
    }));
    const topPeople = peopleRows.slice(0, 5).map(([person, rows]) => ({
      person,
      total: rows.length,
      standards: uniqueCount(rows, (item) => item.standard),
      profiles: uniqueCount(rows, (item) => item.qualificationName),
    }));
    return {
      total: competencies.length,
      approved,
      approvedRate,
      attention,
      people,
      profiles,
      standards,
      topProfiles,
      topStandards,
      matrix,
      standardColumns,
      topPeople,
    };
  }, [competencies]);

  const donutSeries = analytics.topProfiles.map((item) => item.value);
  const donutLabels = analytics.topProfiles.map((item) => item.label);
  const barSeries = [{ name: 'Competency holders', data: analytics.topStandards.map((item) => item.value) }];
  const barLabels = analytics.topStandards.map((item) => item.label);
  // Softer, CBQA teal-forward qualitative palette (brand teal #1f656c/#2a8a93 as the anchor,
  // desaturated companions instead of the previous saturated red/pink outlier).
  const chartColors = ['#1f656c', '#2a8a93', '#6f9bc7', '#8c5ad8', '#ca7b20', '#5fb3a3', '#8a97a8', '#4aa6b5'];
  const matrixGridStyle = {
    gridTemplateColumns: `minmax(190px, 1.4fr) 70px minmax(150px, 1fr) repeat(${Math.max(analytics.standardColumns.length, 1)}, minmax(110px, 1fr))`,
  };

  return (
    <div className="container-xl hris-dashboard-page">
      <div className="page-header mb-4">
        <h2 className="page-title mb-1">HRIS Dashboard</h2>
        <p className="text-muted mb-0">
          Live overview from CRM employee, department, and competency master data.
        </p>
      </div>

      <div className="row g-3">
        {cards.map(({ key, label, icon: Icon, color }) => (
          <div className="col-sm-6 col-xl-3" key={key}>
            <div className="card h-100" style={{ borderTop: `3px solid ${color}` }}>
              <div className="card-body d-flex align-items-center gap-3">
                <div className="rounded-3 p-2" style={{ color, background: `${color}18` }}>
                  <Icon size={22} />
                </div>
                <div>
                  <div className="text-muted small">{label}</div>
                  <div className="h2 mb-0">{metrics[key] ?? '—'}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="hris-competency-panel mt-4">
        <div className="hris-competency-heading">
          <div>
            <h3>Competency Coverage Dashboard</h3>
            <p>Who owns which competency, and which competency is held by whom.</p>
          </div>
          <span className={usingDemo ? 'hris-data-pill hris-data-pill--demo' : 'hris-data-pill'}>
            {usingDemo ? 'Demo data' : 'Live data'}
          </span>
        </div>

        {competencyError && (
          <div className="alert alert-warning mb-3" role="status">
            {competencyError}
          </div>
        )}

        <div className="hris-competency-mini-grid">
          <MiniMetric icon={Users} label="People with competency" value={analytics.people} />
          <MiniMetric icon={Award} label="Competency records" value={analytics.total} tone="blue" />
          <MiniMetric icon={ShieldCheck} label="Approved records" value={`${analytics.approved} (${analytics.approvedRate}%)`} tone="green" />
          <MiniMetric icon={Layers3} label="Standards covered" value={analytics.standards} tone="purple" />
          <MiniMetric icon={Clock3} label="Need attention" value={analytics.attention} tone="orange" />
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12 col-xl-8">
            <div className="hris-chart-card">
              <div className="hris-chart-card__header">
                <div>
                  <h4>Standard Coverage</h4>
                  <p>Number of approved competency holders per standard.</p>
                </div>
                <span>{analytics.topStandards.length} standards</span>
              </div>
              {isCompetencyLoading ? (
                <div className="hris-chart-empty">Loading chart...</div>
              ) : barLabels.length > 0 ? (
                <ReactApexChart
                  type="bar"
                  height={280}
                  series={barSeries}
                  options={buildChartOptions({ labels: barLabels, colors: ['#1f656c'], horizontal: true })}
                />
              ) : (
                <div className="hris-chart-empty">No approved competency records yet.</div>
              )}
            </div>
          </div>
          <div className="col-12 col-xl-4">
            <div className="hris-chart-card">
              <div className="hris-chart-card__header">
                <div>
                  <h4>Competency Profile Mix</h4>
                  <p>Distribution by qualification profile.</p>
                </div>
                <span>{analytics.profiles} profiles</span>
              </div>
              {isCompetencyLoading ? (
                <div className="hris-chart-empty">Loading chart...</div>
              ) : donutSeries.length > 0 ? (
                <>
                  <ReactApexChart
                    type="donut"
                    height={240}
                    series={donutSeries}
                    options={buildChartOptions({ labels: donutLabels, colors: chartColors, series: donutSeries })}
                  />
                  <DonutLegend items={analytics.topProfiles} colors={chartColors} />
                </>
              ) : (
                <div className="hris-chart-empty">No competency profile data.</div>
              )}
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12 col-xl-9">
            <div className="hris-chart-card hris-matrix-card">
              <div className="hris-chart-card__header">
                <div>
                  <h4>People Capability Matrix</h4>
                  <p>Employee coverage across standards.</p>
                </div>
                <span>Employee vs standard</span>
              </div>
              <div className="hris-matrix">
                {/* Grid width is data-driven (3 real standards today, up to 6 max) — a
                    hardcoded repeat(6, ...) left empty unstyled tracks and a phantom
                    scrollbar whenever fewer than 6 standards had approved competencies. */}
                <div className="hris-matrix__row hris-matrix__row--head" style={matrixGridStyle}>
                  <span>Employee</span>
                  <span>Total</span>
                  <span>Primary role</span>
                  {analytics.standardColumns.map((standard) => <span key={standard}>{standard}</span>)}
                </div>
                {analytics.matrix.map((row) => (
                  <div className="hris-matrix__row" key={row.person} style={matrixGridStyle}>
                    <strong>{row.person}</strong>
                    <span className="hris-matrix__count">{row.total}</span>
                    <span className="hris-matrix__role">{row.primaryRole}</span>
                    {row.standards.map((hasCompetency, index) => (
                      <span
                        key={`${row.person}-${analytics.standardColumns[index]}`}
                        className={hasCompetency ? 'is-covered' : 'is-empty'}
                        title={hasCompetency ? 'Competency held' : 'No matching competency'}
                      >
                        {hasCompetency ? <Check size={15} strokeWidth={3} /> : '—'}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-12 col-xl-3">
            <div className="hris-chart-card hris-top-people">
              <div className="hris-chart-card__header">
                <div>
                  <h4>Top Multi-skilled</h4>
                  <p>People with broadest coverage.</p>
                </div>
              </div>
              <div className="hris-top-people__list">
                {analytics.topPeople.map((person, index) => (
                  <div className="hris-top-people__item" key={person.person}>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{person.person}</strong>
                      <small>{person.profiles} profiles across {person.standards} standards</small>
                    </div>
                    <b>{person.total}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CompetencyList
        items={competencies}
        isLoading={isCompetencyLoading}
        error={usingDemo ? '' : competencyError}
      />
    </div>
  );
};

export default HRISDashboard;
